import { Stack, Typography, MenuList, Popover, useTheme, ListItemText, alpha } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';

import { Iconify, Scrollbar, SearchBar } from '@app/components';
import { CustomAvatar } from '@app/components/custom-avatar';
import { Strings } from '@app/constants';
import { useRequestState, useSearch } from '@app/hooks';
import {
  selectAddCardMembersState,
  selectBoardDetailsState,
  selectCardDetailState,
  selectDeleteMemberFromCardState,
} from '../selector';
import { addCardMember, deleteMemberFromCard } from '../slice';

interface Props {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  handleClose: () => void;
  cardId: number;
}

const AddMemberPopover = ({ open, anchorEl, handleClose, cardId }: Props) => {
  const theme = useTheme();
  const id = open ? 'simple-popover' : undefined;
  const dispatch = useDispatch();

  const { data } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });

  const { data: cardDetails } = useRequestState({
    stateSelector: selectCardDetailState,
    errorShown: true,
  });

  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);

  useEffect(() => {
    if (cardDetails?.members) {
      const memberIds = cardDetails.members.map((member: any) => member.membersUserId);
      setSelectedMembers(memberIds);
    }
  }, [cardDetails]);

  useRequestState({
    stateSelector: selectAddCardMembersState,
    errorShown: true,
  });

  const searchProps = useSearch({
    data: data?.members,
    fields: ['firstName', 'lastName'],
  });

  const handleMemberToggle = (id: number) => {
    const isSelected = selectedMembers.includes(id);
    if (isSelected) {
      dispatch(deleteMemberFromCard({ userId: id, cardId: Number(cardId) }));
      setSelectedMembers(selectedMembers.filter((member) => member !== id));
    } else {
      dispatch(addCardMember({ cardId: Number(cardId), membersUserIds: [id] }));
      setSelectedMembers([...selectedMembers, id]);
    }
  };

  useRequestState({
    stateSelector: selectDeleteMemberFromCardState,
    errorShown: true,
  });
  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      sx={{
        '& .MuiPaper-root': {
          width: '300px',
          height: '50%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: alpha(theme.palette.error.contrastText, 0.1),
          backdropFilter: 'blur(10px)',
          borderRadius: '10px',
          boxShadow: theme.shadows[5],
        },
      }}
    >
      <Stack sx={{ alignItems: 'center' }}>
        <Typography>{Strings.taskBoard.members}</Typography>
      </Stack>
      <Stack p={2}>
        <SearchBar placeholder="search" onChange={searchProps.handleSearchChange} />
      </Stack>
      <Scrollbar>
        <Stack ml={2}>
          {searchProps?.filteredRows && searchProps?.filteredRows?.length > 0 ? (
            searchProps?.filteredRows?.map((manager: any, index: number) => {
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              const fullName = `${manager.firstName} ${manager.lastName}`;
              const memberId = manager.membersUserId;

              return (
                <MenuList
                  key={index}
                  onClick={() => {
                    handleMemberToggle(memberId);
                  }}
                  sx={{
                    backgroundColor: selectedMembers.includes(memberId)
                      ? alpha(theme.palette.taskboard.raisinBlack, 0.5)
                      : alpha(theme.palette.error.contrastText, 0.1),
                    mb: 0.5,
                    pl: 1,
                    mr: 0.5,
                    backdropFilter: 'blur(10px)',
                    borderRadius: '10px',
                    boxShadow: theme.shadows[5],
                  }}
                >
                  <Stack sx={{ flexDirection: 'row' }} gap={2.5}>
                    <CustomAvatar
                      name={fullName ?? 'NA'}
                      src={manager?.profileImage}
                      sx={{
                        fontSize: 13,
                        backgroundColor: theme.palette.taskboard.raisinBlack,
                        color: theme.palette.error.contrastText,
                      }}
                    />
                    <Stack sx={{ alignSelf: 'center' }}>
                      <ListItemText primary={fullName} />
                    </Stack>
                    {selectedMembers.includes(memberId) && (
                      <Stack
                        mt={1}
                        sx={{
                          marginLeft: 'auto',
                          marginRight: 1.5,
                        }}
                      >
                        <Iconify icon="charm:cross" />
                      </Stack>
                    )}
                  </Stack>
                </MenuList>
              );
            })
          ) : (
            <Stack>{Strings.taskBoard.noMemberFoundInThisBoard}</Stack>
          )}
        </Stack>
      </Scrollbar>
    </Popover>
  );
};

export { AddMemberPopover };
